<script lang="ts" setup>
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import PartnerActivity from "./components/PartnerActivity.vue";
import IssuesList from "./components/IssuesList.vue";
import OrganizationSwitchModal from "@/views/shared/components/modals/OrganizationSwitchModal.vue";
import { usePartnerActivities } from "@/queries/activities";
import { usePartnerIssues } from "@/queries/partner-issues";
import { usePartnerClientSummaries } from "@/queries/partner-clients";
import { useSwitchOrganisation } from "@/queries/orgs";

const route = useRoute();
const clientId = computed(() => String(route.params.id));

// =====================================================================
// Client record — matched by id from the shared partner-client summaries (real metrics, same
// source as the Clients list + dashboard). If the id isn't one of the caller's clients (stale
// link / not authorised), we show a not-found state rather than inventing a record.
// =====================================================================
const { clients, clientsLoading } = usePartnerClientSummaries();
const isLoading = computed(() => clientsLoading.value);
const client = computed(() => clients.value.find((c) => c.id === clientId.value) ?? null);
// Only "not found" once the load has finished and the id genuinely isn't in the list.
const notFound = computed(() => !clientsLoading.value && client.value === null);

// =====================================================================
// Client activity — REAL data, scoped to this client org via the partner activities endpoint.
// =====================================================================
const { activities: clientActivities, activitiesLoading } = usePartnerActivities({
  clientOrgId: clientId,
  take: 10,
});

// =====================================================================
// Client issues — REAL data, scoped to this client org.
// =====================================================================
const { issues: clientIssues } = usePartnerIssues({ clientOrgId: clientId });

const openIssueCount = computed(() => clientIssues.value.filter((i) => !i.resolved).length);

// =====================================================================
// Header presentation
// =====================================================================
const statusBadgeClass = computed(() => {
  switch (client.value?.status) {
    case "active": return "bg-success";
    case "pending": return "bg-warning";
    case "inactive": return "bg-secondary";
    default: return "bg-secondary";
  }
});

const healthIconClass = computed(() => {
  switch (client.value?.healthStatus) {
    case "healthy": return "bi-check-circle-fill text-success";
    case "warning": return "bi-exclamation-triangle-fill text-warning";
    case "error": return "bi-x-circle-fill text-danger";
    default: return "bi-question-circle-fill text-secondary";
  }
});

const successRateColor = computed(() => {
  const r = client.value?.successRate ?? 0;
  return r >= 90 ? "text-success" : r >= 80 ? "text-warning" : "text-danger";
});

function formatDate(value?: string) {
  return value ? new Date(value).toLocaleDateString() : "—";
}

// =====================================================================
// Switch into the client's org from the header CTA.
// =====================================================================
const showSwitchModal = ref(false);
const { switchOrganisationPending } = useSwitchOrganisation();
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <div v-if="isLoading && !client" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else-if="notFound" class="text-center py-5">
        <i class="bi bi-people display-1 text-muted"></i>
        <h3 class="mt-3 text-muted">Client not found</h3>
        <p class="text-muted">
          This client isn't one of yours, or it may have been removed.
        </p>
        <router-link to="/partners/clients" class="bb-btn-secondary-cta">
          <i class="bi bi-arrow-left me-2"></i>Back to Clients
        </router-link>
      </div>

      <template v-else-if="client">
        <PageHeader
          :title="client.organizationName"
          :breadcrumbs="[
            { label: 'Dashboard', href: '/' },
            { label: 'Partners', href: '/partners' },
            { label: 'Clients', href: '/partners/clients' },
            { label: client.organizationName },
          ]"
        >
          <template #actions>
            <button
              class="bb-btn-primary-cta"
              :disabled="switchOrganisationPending"
              @click="showSwitchModal = true"
            >
              <i class="bi bi-box-arrow-in-right me-2"></i>Switch to Org
            </button>
            <router-link to="/partners/clients" class="bb-btn-secondary-cta d-none d-md-inline">
              <i class="bi bi-arrow-left me-2"></i>All Clients
            </router-link>
          </template>
        </PageHeader>

        <div class="d-flex align-items-center gap-3 mt-2 mb-4">
          <span class="badge text-uppercase" :class="statusBadgeClass">{{ client.status }}</span>
          <span><i :class="['bi', healthIconClass]" :title="client.healthStatus"></i></span>
          <small class="text-muted">Partner since {{ formatDate(client.joinedDate) }}</small>
        </div>

        <!-- Stat row -->
        <div class="row g-4 mb-4">
          <div class="col-6 col-md-3">
            <div class="card text-center">
              <div class="card-body">
                <i class="bi bi-folder fs-1 text-secondary mb-2"></i>
                <h3 class="mb-1">{{ client.projectCount }}</h3>
                <p class="card-subtitle text-muted mb-0">Projects</p>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center">
              <div class="card-body">
                <i class="bi bi-play-circle fs-1 text-info mb-2"></i>
                <h3 class="mb-1">{{ client.totalRuns.toLocaleString() }}</h3>
                <p class="card-subtitle text-muted mb-0">Total Runs</p>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center">
              <div class="card-body">
                <i class="bi bi-check-circle fs-1 mb-2" :class="successRateColor"></i>
                <h3 class="mb-1" :class="successRateColor">{{ client.successRate.toFixed(1) }}%</h3>
                <p class="card-subtitle text-muted mb-0">Success Rate</p>
              </div>
            </div>
          </div>
          <div class="col-6 col-md-3">
            <div class="card text-center">
              <div class="card-body">
                <i
                  class="bi bi-exclamation-triangle fs-1 mb-2"
                  :class="openIssueCount > 0 ? 'text-danger' : 'text-success'"
                ></i>
                <h3 class="mb-1" :class="openIssueCount > 0 ? 'text-danger' : 'text-success'">
                  {{ openIssueCount }}
                </h3>
                <p class="card-subtitle text-muted mb-0">Open Issues</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Activity + Issues -->
        <div class="row g-4">
          <div class="col-12 col-lg-8">
            <div class="card h-100">
              <div class="card-body">
                <h2 class="card-title">Recent Activity</h2>
                <PartnerActivity
                  :activities="clientActivities"
                  :loading="activitiesLoading"
                />
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <div class="card h-100">
              <div class="card-body">
                <h2 class="card-title">Issues</h2>
                <IssuesList :issues="clientIssues" :show-view-all="false" />
              </div>
            </div>
          </div>
        </div>

        <!-- Contact -->
        <div class="card mt-4">
          <div class="card-body">
            <h2 class="card-title">Contact</h2>
            <div class="row">
              <div class="col-md-4">
                <small class="text-muted d-block">Name</small>
                <span>{{ client.contactName }}</span>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Email</small>
                <a :href="`mailto:${client.contactEmail}`">{{ client.contactEmail }}</a>
              </div>
              <div class="col-md-4">
                <small class="text-muted d-block">Last activity</small>
                <span>{{ formatDate(client.lastActivity) }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </PanelCard>
  </PanelGrid>

  <OrganizationSwitchModal
    v-if="client"
    :show="showSwitchModal"
    @update:show="showSwitchModal = $event"
    :target-organization="{ id: client.id, name: client.organizationName }"
    :target-route="{ name: 'home' }"
    context="activity"
  />
</template>
